import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import CustomTextarea from "../Forms/CustomTextarea";
import TableWithPagination from "../Tables/TableWithPagination";
import { useEffect, useState } from "react";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useEscapeKey from "@hooks/useEscapeKey";

const NoticeInformationViewModal = ({
  isOpen,
  onClose,
  noticeInfoComponents = [],
  noticeInfo,
  "data-testid": dataTestId = "notice-information-view-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [tableData, setTableData] = useState([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY. NOTICE_INFORMATION_VIEW_LIST, DEFAULT_ITEMS_PER_PAGE);

  useEffect(() => {
  const total = noticeInfoComponents.length;
  setTotalFiles(total);

  const pages = Math.ceil(total / itemsPerPage) || 1;
  setTotalPages(pages);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const paginatedData = noticeInfoComponents.slice(startIndex, endIndex);
  setTableData(paginatedData);
}, [noticeInfoComponents, currentPage, itemsPerPage]);

  // Table configuration for read-only display
  const tableConfig = {
    componentName: {
      title: t("project.notice-infor.component-name"),
      style: {
        width: "15%",
      },
      customRender: (value, row) => (
        <div className={`text-sm ${colors.textPrimary}`}>
          <div>
            {row.componentName || row.component}
            {row.componentVersion ? ` (${row.componentVersion})` : ""}
          </div>
          <div className={`text-xs ${colors.textMuted}`}>{row.licenseName}</div>
        </div>
      ),
    },
    url: {
      title: t("common.url"),
      customRender: (value, row) => <div className={`truncate p-2 text-sm ${colors.textPrimary}`}>{row.url || "-"}</div>,
    },
    copyrightInfo: {
      title: t("open-source.copyright"),
      customRender: (value, row) => (
        <div className={`truncate p-2 text-sm ${colors.textPrimary}`}>{row.copyrightInfo || "-"}</div>
      ),
    },
    licenseText: {
      title: t("open-source.license-text"),
      customRender: (value, row) => <div className={`truncate p-2 text-sm ${colors.textPrimary}`}>{row.licenseText || "-"}</div>,
    },
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
        handleItemsPerPageChange(newItemsPerPage);
        setCurrentPage(1); // Reset to page 1 when changing number of rows
    };

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <div
      data-testid={`${dataTestId}-overlay`}
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center p-4 backdrop-blur-[1px] overflow-x-auto`}
    >
      <div
        data-testid={dataTestId}
        className={`border ${colors.borderPrimary} ${colors.bgModal} min-h-[50vh] max-h-[90vh] w-[1200px] min-w-[1200px] flex-shrink-0 flex flex-col rounded-lg shadow-xl`}
      >
        {/* Header */}
        <div className={`px-6 py-6 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>
                {t("project.notice-infor.notice-infor-list")}
              </h2>
            </div>
            <button type="button" data-testid={`${dataTestId}-close-button`} onClick={onClose} className={`transition-colors ${colors.iconMuted} ${colors.hoverTextSecondary}`}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 space-y-4" data-testid={`${dataTestId}-content`}>
            {/* Notice Header Section */}
            <div className="mb-6" data-testid={fieldContainerTestId("notice-header")}>
              <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`} data-testid={fieldLabelTestId("notice-header")}>
                {t("project.notice-infor.notice-header")}
              </label>
              <CustomTextarea
                value={noticeInfo?.noticeInfoHeader || ""}
                onChange={() => { }} // Disabled - no onChange handler
                rows={4}
                size="md"
                disabled={true}
                className="cursor-not-allowed opacity-75"
              />
            </div>

            {noticeInfoComponents.length === 0 ? (
              <div className="flex items-center justify-center py-8" data-testid={`${dataTestId}-empty`}>
                <div className="text-center">
                  <div className={`mb-2 text-lg ${colors.iconMuted}`}>{t("common.no-data-to-display")}</div>
                  <div className={`text-sm ${colors.textMuted}`}>{t("project.notice-infor.no-notice-information")}</div>
                </div>
              </div>
            ) : (
              <div data-testid={`${dataTestId}-table`}>
              <TableWithPagination
                dataTestId={`${dataTestId}-table`}
                columns={["componentName", "url", "copyrightInfo", "licenseText"]}
                data={tableData}
                colSize={12}
                columnConfig={tableConfig}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                totalData={totalFiles}
                maxRowsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChangeWithReset}
                showPagination={true}
                showItemsPerPageSelector={true}
                rowKey="verificationReportDetailSeq"
              />
              </div>
            )}
          </div>

          {/* Footer - Fixed at bottom */}
          <div className="flex-shrink-0 py-4">
            <div className={`h-px w-full ${colors.bgDivider} mb-4`}></div>

            {/* Action Buttons */}
            <div className={`${colors.borderPrimary} px-6`}>
              <div className="flex items-center justify-end">
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    data-testid={`${dataTestId}-cancel-button`}
                    onClick={onClose}
                    className={`rounded-lg border px-4 py-2 text-sm ${colors.textActive} hover:${colors.bgSecondary} transition-colors`}
                  >
                    {t("common.cancel-button")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoticeInformationViewModal;
